/**
 * ShopView view component.
 * @module components/theme/View/ShopView
 */
import React, { useState } from 'react';
import './ShopView.scss';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import {
  Container as SemanticContainer,
  Grid,
  Label,
  List,
  Segment,
} from 'semantic-ui-react';
import config from '@plone/volto/registry';
import Unauthorized from '@plone/volto/components/theme/Unauthorized/Unauthorized';
import { getSchema } from '@plone/volto/actions/schema/schema';
import { getWidget } from '@plone/volto/helpers/Widget/utils';
import { hasBlocksData } from '@plone/volto/helpers/Blocks/Blocks';
import { flattenToAppURL, getBaseUrl } from '@plone/volto/helpers/Url/Url';
import { Helmet } from '@plone/volto/helpers/Helmet/Helmet';
import { default as Icon } from '@plone/volto/components/theme/Icon/Icon';
import Image from '@plone/volto/components/theme/Image/Image';
import rightArrowSVG from '@plone/volto/icons/right-key.svg';
import find from 'lodash/find';
import isEqual from 'lodash/isEqual';
import ShopClosedDatesCalendar from './ShopClosedDatesCalendar';
import Articles from './Articles';
import { TimetableViewWidget, TimetableClassicViewWidget } from '../..';
import { ClosedDates } from '../..';
import Prices from './Prices';
import { Plug } from '@plone/volto/components/manage/Pluggable';

const messages = defineMessages({
  articles: {
    id: 'articles',
    defaultMessage: 'Articles',
  },
  back: {
    id: 'back',
    defaultMessage: 'Back',
  },
  calendar: {
    id: 'calendar',
    defaultMessage: 'Calendar',
  },
  otherChanges: {
    id: 'Make other changes',
    defaultMessage:
      'Should there be other configurations you need to do, but you can not do them ' +
      'through this application, please contact Apanymantel as usual or send an ' +
      'e-mail to info@apanymantel.com.',
  },
  orders: {
    id: 'orders',
    defaultMessage: 'Orders',
  },
  prices: {
    id: 'prices',
    defaultMessage: 'Prices',
  },
  shippingAgentsSettings: {
    id: 'shippingAgentsSettings',
    defaultMessage: 'Shipping Agents Settings',
  },
  shop: {
    id: 'shop',
    defaultMessage: 'Shop',
  },
});
/**
 * ShopView view component class.
 * @function ShopView
 * @params {object} content Content object.
 * @return {string} Markup of the component .
 */
const ShopView = (props) => {
  const {
    data = {},
    content,
    location: { pathname },
  } = props;

  const [state, setState] = useState(0);
  const intl = useIntl();
  const dispatch = useDispatch();
  const { views } = config.widgets;

  // parts from @plone/volto/components/theme/View/DefaultView.jsx
  const contentLoaded = content && !isEqual(Object.keys(content), ['lock']);
  const contentSchema = useSelector((state) => state.schema?.schema);
  const fieldsetsToExclude = [
    'categorization',
    'dates',
    'ownership',
    'settings',
  ];
  const fieldsets = contentSchema?.fieldsets.filter(
    (fs) => !fieldsetsToExclude.includes(fs.id),
  );
  const myShops = useSelector((state) => state.my_shops.my_shops || []);
  const contentId = flattenToAppURL(content['@id']);
  const isShopstaff =
    myShops.filter((myShop) => myShop['@id'] === contentId).length > 0;
  // const dispatch = useDispatch();
  // const isShopstaff = useSelector((state) => {
  //   const userId = state.userSession.token
  //     ? jwtDecode(state.userSession.token).sub
  //     : null;
  //   const role = state.sharing.data.entries.filter(
  //     (entry) => userId && entry.id === userId && entry.roles.ShopstaffMember,
  //   );
  //   return role.length > 0;
  // });
  // React.useEffect(() => {
  //   dispatch(getSharing(pathname));
  // }, [dispatch, pathname]);

  const shippingAgentsAction = useSelector((state) =>
    find(state.actions.actions.object, {
      id: 'shipping_agents_settings',
    }),
  );
  const path = getBaseUrl(pathname);

  React.useEffect(() => {
    content?.['@type'] &&
      !hasBlocksData(content) &&
      dispatch(getSchema(content['@type'], location.pathname));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const Container =
    config.getComponent({ name: 'Container' }).component || SemanticContainer;

  const pricesPage = (
    <div>
      <button onClick={() => setState(0)}>
        <i className="left arrow icon"></i>
        <FormattedMessage id="back" defaultMessage="Back" />
      </button>
      <Prices data={data} pathname={pathname} />
    </div>
  );

  const actions = useSelector((state) => state.actions.actions);
  // If the 'edit' action exists, the user has write permission
  const canEdit = actions?.object?.find((action) => action.id === 'edit');

  if (!isShopstaff && !canEdit) {
    return <Unauthorized />;
  }

  return (
    <section className="shopview">
      {shippingAgentsAction && (
        <Plug
          pluggable="toolbar-more-menu-list"
          id="shipping-agents-settings"
          order="1000"
        >
          <li>
            <Link to={`${path}/shipping-agents-settings`}>
              <div>
                <span className="pastanaga-menu-label">
                  <FormattedMessage
                    id="shippingAgentsSettings"
                    defaultMessage="Shipping Agents Settings"
                  />
                </span>
                <span className="pastanaga-menu-value" />
              </div>
              <Icon name={rightArrowSVG} size="24px" />
            </Link>
          </li>
        </Plug>
      )}
      <Plug pluggable="toolbar-more-menu-list" id="orders" order="1010">
        <li>
          <Link to={`${path}/orders`}>
            <div>
              <span className="pastanaga-menu-label">
                <FormattedMessage id="orders" defaultMessage="Orders" />
              </span>
              <span className="pastanaga-menu-value" />
            </div>
            <Icon name={rightArrowSVG} size="24px" />
          </Link>
        </li>
      </Plug>
      {state === 0 && (
        <Plug pluggable="main.toolbar.bottom">
          <button onClick={() => setState(3)}>
            <FormattedMessage id="prices" defaultMessage="Prices" />
          </button>
        </Plug>
      )}
      <Helmet title={content.title} />
      <article id="content">
        {!isShopstaff && (
          <>
            {state === 0 && (
              <Container id="page-document">
                {fieldsets?.map((fs) => {
                  console.log('fieldset', fs.id);
                  if (['discounts'].includes(fs.id)) {
                    return null;
                  }
                  return (
                    <div className="fieldset" key={fs.id}>
                      {fs.id !== 'default' && <h2>{fs.title}</h2>}
                      {fs.fields?.map((f, key) => {
                        let field = {
                          ...contentSchema?.properties[f],
                          id: f,
                          widget: getWidget(f, contentSchema?.properties[f]),
                        };
                        let Widget = views?.getWidget(field);
                        return f !== 'title' ? (
                          <Grid celled="internally" key={key}>
                            <Grid.Row>
                              <Label title={field.id}>{field.title}:</Label>
                            </Grid.Row>
                            <Grid.Row>
                              <Segment basic>
                                <Widget value={content[f]} />
                              </Segment>
                            </Grid.Row>
                          </Grid>
                        ) : (
                          <Widget key={key} value={content[f]} />
                        );
                      })}
                    </div>
                  );
                })}
              </Container>
            )}
            {state === 3 && pricesPage}
          </>
        )}
        {false && (
          <>
            {state === 0 && (
              <section className="content-core isAdmin">
                <article>
                  {content.description && (
                    <p className="documentDescription">{content.description}</p>
                  )}
                </article>
                <article>
                  {content.logo && (
                    <Image
                      className="document-image"
                      src={content.logo.scales.thumb.download}
                      alt=""
                    />
                  )}
                </article>
                <article>
                  {content.shop_picture && (
                    <Image
                      className="document-image"
                      src={content.shop_picture.scales.thumb.download}
                      alt=""
                    />
                  )}
                </article>
                <article>
                  {content.summary && (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: content.summary.data,
                      }}
                    />
                  )}
                </article>
                <article>
                  {content.directions_for_use && (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: content.directions_for_use.data,
                      }}
                    />
                  )}
                </article>
                <article>
                  {content.specialities && (
                    <p className="documentDescription">
                      {content.specialities}
                    </p>
                  )}
                </article>
                <article>
                  {content.article_categories && (
                    <ul className="shop-categories">
                      {content.article_categories.map((cat) => (
                        <li>
                          <List.Item key={cat}>{cat}</List.Item>
                        </li>
                      ))}
                    </ul>
                  )}
                </article>
                <article>
                  {content.minimum_ratings_to_show && (
                    <p className="documentDescription">
                      {content.minimum_ratings_to_show}
                    </p>
                  )}
                </article>
                <article>
                  {content.corporate_name && (
                    <p className="documentDescription">
                      {content.corporate_name}
                    </p>
                  )}
                </article>
                <article>
                  {content.bank_account && (
                    <p className="documentDescription">
                      {content.bank_account}
                    </p>
                  )}
                </article>
                <article>
                  {content.bank_bic_code && (
                    <p className="documentDescription">
                      {content.bank_bic_code}
                    </p>
                  )}
                </article>
                <article>
                  {content.year_founded && (
                    <p className="documentDescription">
                      {content.year_founded}
                    </p>
                  )}
                </article>
                <article>
                  {content.web_site && (
                    <p className="documentDescription">{content.web_site}</p>
                  )}
                </article>
                <article>
                  {content.belongs_to_store_chain && (
                    <p className="documentDescription">
                      {content.belongs_to_store_chain}
                    </p>
                  )}
                </article>
                <article>
                  {content.intermediary_fee && (
                    <p className="documentDescription">
                      {content.intermediary_fee}
                    </p>
                  )}
                </article>
                <article>
                  {content.intermediary_fee_pickup && (
                    <p className="documentDescription">
                      {content.intermediary_fee_pickup}
                    </p>
                  )}
                </article>
                <article>
                  {content.exclude_shipping_costs_from_fee && (
                    <p className="documentDescription">
                      {content.exclude_shipping_costs_from_fee}
                    </p>
                  )}
                </article>
                <article>
                  {content.vat_reg_number && (
                    <p className="documentDescription">
                      {content.vat_reg_number}
                    </p>
                  )}
                </article>
                <article>
                  {/* {content.vat_rate && ( */}
                  {/*   <p className="documentDescription">{content.vat_rate}</p> */}
                  {/* )} */}
                  {content.address && (
                    <p className="documentDescription">{content.address}</p>
                  )}
                </article>
                <article>
                  {content.post_code && (
                    <p className="documentDescription">{content.post_code}</p>
                  )}
                </article>
                <article>
                  {content.city && (
                    <p className="documentDescription">{content.city}</p>
                  )}
                </article>
                <article>
                  {content.county && (
                    <p className="documentDescription">{content.county}</p>
                  )}
                </article>
                <article>
                  {content.country && (
                    <p className="documentDescription">{content.country}</p>
                  )}
                </article>
                <article>
                  {/* {content.timezone && ( */}
                  {/*   <p className="documentDescription">{content.timezone}</p> */}
                  {/* )} */}
                </article>
                <article>
                  {content.phone_no && (
                    <p className="documentDescription">{content.phone_no}</p>
                  )}
                </article>
                <article>
                  {content.email && (
                    <p className="documentDescription">{content.email}</p>
                  )}
                </article>
                <article>
                  {content.invoicing_emails && (
                    <List>
                      {content.invoicing_emails.map((email) => (
                        <List.Item key={email}>{email}</List.Item>
                      ))}
                    </List>
                  )}
                </article>
                {/* Owner/partner data */}
                <article>
                  {content.owner_firstname && (
                    <p className="documentDescription">
                      {content.owner_firstname}
                    </p>
                  )}
                </article>
                <article>
                  {content.owner_lastname && (
                    <p className="documentDescription">
                      {content.owner_lastname}
                    </p>
                  )}
                </article>
                <article>
                  {content.owner_lastname2 && (
                    <p className="documentDescription">
                      {content.owner_lastname2}
                    </p>
                  )}
                </article>
                <article>
                  {content.id_number && (
                    <p className="documentDescription">{content.id_number}</p>
                  )}
                </article>
                <article>
                  {content.contact_firstname && (
                    <p className="documentDescription">
                      {content.contact_firstname}
                    </p>
                  )}
                </article>
                <article>
                  {content.contact_lastname && (
                    <p className="documentDescription">
                      {content.contact_lastname}
                    </p>
                  )}
                </article>
                <article>
                  {content.contact_lastname2 && (
                    <p className="documentDescription">
                      {content.contact_lastname2}
                    </p>
                  )}
                </article>
                <article>
                  {content.contact_mobile_no && (
                    <p className="documentDescription">
                      {content.contact_mobile_no}
                    </p>
                  )}
                </article>
                {/* Product Images, */}
                <article>
                  {content.image && (
                    <Image
                      className="document-image"
                      src={content.image.scales.thumb.download}
                      alt=""
                    />
                  )}
                </article>

                {/* {content.main_city_of_shop && ( */}
                {/*   <p className="documentDescription">{content.main_city_of_shop}</p> */}
                {/* )} */}

                {/* {/\* Ordering *\/} */}

                <article>
                  {content.ordering_phone_no && (
                    <p className="documentDescription">
                      {content.ordering_phone_no}
                    </p>
                  )}
                </article>
                <article>
                  {content.ordering_phone_no2 && (
                    <p className="documentDescription">
                      {content.ordering_phone_no2}
                    </p>
                  )}
                </article>
                <article>
                  {content.ordering_email && (
                    <p className="documentDescription">
                      {content.ordering_email}
                    </p>
                  )}
                </article>
                <article>
                  {content.max_orders_day && (
                    <p className="documentDescription">
                      {content.max_orders_day}
                    </p>
                  )}
                </article>
                <article>
                  {content.immediate_preparing_time && (
                    <p className="documentDescription">
                      {content.immediate_preparing_time}
                    </p>
                  )}
                </article>
                <article>
                  {content.same_day_preparing_time && (
                    <p className="documentDescription">
                      {content.same_day_preparing_time}
                    </p>
                  )}
                </article>
                <article>
                  {content.minimum_amount_delivery_order && (
                    <p className="documentDescription">
                      {content.minimum_amount_delivery_order}
                    </p>
                  )}
                </article>
                <article>
                  {content.minimum_order_without_shipping_costs && (
                    <p className="documentDescription">
                      {content.minimum_order_without_shipping_costs}
                    </p>
                  )}
                </article>
                <article>
                  {content.minimum_amount_without_shipping_costs && (
                    <p className="documentDescription">
                      {content.minimum_amount_without_shipping_costs}
                    </p>
                  )}
                </article>
                <article>
                  {content.minimum_amount_pickup_order && (
                    <p className="documentDescription">
                      {content.minimum_amount_pickup_order}
                    </p>
                  )}
                </article>
                <article>
                  {content.no_product_customization && (
                    <p className="documentDescription">
                      {content.no_product_customization}
                    </p>
                  )}
                </article>
                {/* Trade/Delivery Calendar */}
                <article>
                  {content.opening_timetable && (
                    <TimetableViewWidget timetable={content.opening_timetable} />
                  )}
                </article>
                <article>
                  {content.closed_dates && (
                    <ClosedDates closedDates={content.closed_dates} />
                  )}
                </article>
                <article>
                  {content.delivery_timetable && (
                    <TimetableClassicViewWidget timetable={content.delivery_timetable} />
                  )}
                </article>
                <article>
                  {content.delivery_time_interval && (
                    <p className="documentDescription">
                      {content.delivery_time_interval}
                    </p>
                  )}
                </article>
                <article>
                  {content.no_delivery_dates && (
                    <ClosedDates closedDates={content.no_delivery_dates} />
                  )}
                </article>
                <article>
                  {content.limit_time_same_day_order && (
                    <p className="documentDescription">
                      {content.limit_time_same_day_order}
                    </p>
                  )}
                </article>
                <article>
                  {content.time_for_urgent_phone_calls && (
                    <p className="documentDescription">
                      {content.time_for_urgent_phone_calls}
                    </p>
                  )}
                </article>
                <article>
                  {content.ordering_timetable && (
                    <TimetableClassicViewWidget
                      timetable={content.ordering_timetable}
                      orderTo={true}
                    />
                  )}
                </article>
                <article>
                  {/* Warning Message */}
                  {content.warning_message && (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: content.warning_message.data,
                      }}
                    />
                  )}
                </article>
                <article>
                  {content.warning_from_date && (
                    <p className="documentDescription">
                      {content.warning_from_date}
                    </p>
                  )}
                </article>
                <article>
                  {content.warning_to_date && (
                    <p className="documentDescription">
                      {content.warning_to_date}
                    </p>
                  )}
                </article>
              </section>
            )}
            {state === 3 && (
              <>
                <Plug pluggable="main.toolbar.bottom">
                  <button onClick={() => setState(0)}>
                    <FormattedMessage id="shop" defaultMessage="Shop" />
                  </button>
                </Plug>
                <Prices data={data} pathname={pathname} />
              </>
            )}
          </>
        )}
        {isShopstaff && (
          <section className="content-core isShopStaff">
            {state === 0 && (
              <div className="shop-staff-tools">
                <div>
                  <button onClick={() => setState(1)}>
                    <FormattedMessage id="calendar" defaultMessage="Calendar" />
                  </button>
                </div>
                <div>
                  <button onClick={() => setState(2)}>
                    <FormattedMessage id="articles" defaultMessage="Articles" />
                  </button>
                </div>
                <div>
                  <button onClick={() => setState(3)}>
                    <FormattedMessage id="prices" defaultMessage="Prices" />
                  </button>
                </div>
                {/* This is not yet ready */}
                {/* <div> */}
                {/*   <Link to={`${path}/orders`}> */}
                {/*     <button className="ui big orange"> */}
                {/*       <FormattedMessage id="orders" defaultMessage="Orders" /> */}
                {/*     </button> */}
                {/*   </Link> */}
                {/* </div> */}
                <p className="alert-kg">
                  <FormattedMessage
                    id="Make other changes"
                    defaultMessage="Should there be other configurations you need to do, but you can not do them through this application, please contact Apanymantel as usual or send an e-mail to info@apanymantel.com."
                  />
                </p>
              </div>
            )}
            {state === 1 && (
              <div>
                <button onClick={() => setState(0)}>
                  <i className="left arrow icon"></i>
                  <FormattedMessage id="back" defaultMessage="Back" />
                </button>
                <ShopClosedDatesCalendar pathname={pathname} monthsQty={18} />
              </div>
            )}
            {state === 2 && (
              <div>
                <button onClick={() => setState(0)}>
                  <i className="left arrow icon"></i>
                  <FormattedMessage id="back" defaultMessage="Back" />
                </button>
                <Articles data={data} pathname={pathname} />
              </div>
            )}
            {state === 3 && pricesPage}
          </section>
        )}
      </article>
    </section>
  );
};

/**
 * Property Types
 * @property {Object} propTypes Property types.
 * @static
 */
ShopView.propTypes = {
  content: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }).isRequired,
};

export default ShopView;
